import type { IdentifierValidation } from './types';

/**
 * Validadores COMPARTILHADOS do login. Puros (sem dependência de React/i18n):
 * recebem o valor e devolvem `null` quando válido ou a CHAVE da mensagem de
 * erro (resolvida pelo chamador via i18n). Mantém o login-kit desacoplado.
 */

/** Chaves de erro de validação — espelham `LoginKitMessages.validation`. */
export type ValidationKey =
  | 'required'
  | 'email'
  | 'cnpjOrEmail'
  | 'passwordRequired'
  | 'passwordMin';

/** RFC-lite: suficiente para UI, sem falsos negativos comuns. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

/**
 * Valida um CNPJ pelos dois dígitos verificadores (módulo 11). Aceita o número
 * com ou sem máscara (pontos, barra e traço são ignorados).
 */
export function isValidCnpj(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 14) return false;
  // rejeita sequências repetidas (00000000000000, 11111111111111, ...)
  if (/^(\d)\1{13}$/.test(digits)) return false;

  const checkDigit = (length: number): number => {
    const weights =
      length === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < length; i += 1) sum += Number(digits[i]) * weights[i];
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  return checkDigit(12) === Number(digits[12]) && checkDigit(13) === Number(digits[13]);
}

/** O valor parece ser uma tentativa de CNPJ (e não um e-mail)? */
function looksLikeCnpj(value: string): boolean {
  return !value.includes('@');
}

/**
 * Valida o campo identificador segundo a estratégia do portal.
 * @returns chave de erro de validação ou `null` quando válido.
 */
export function validateIdentifier(
  value: string,
  kind: IdentifierValidation = 'email',
): ValidationKey | null {
  const trimmed = value.trim();
  if (!trimmed) return 'required';

  switch (kind) {
    case 'email':
      return isValidEmail(trimmed) ? null : 'email';
    case 'cnpj-or-email':
      if (looksLikeCnpj(trimmed)) return isValidCnpj(trimmed) ? null : 'cnpjOrEmail';
      return isValidEmail(trimmed) ? null : 'cnpjOrEmail';
    case 'text':
    default:
      return null;
  }
}

/**
 * Valida a senha: obrigatória e com comprimento mínimo.
 * @returns chave de erro de validação ou `null` quando válida.
 */
export function validatePassword(value: string, minLength = 6): ValidationKey | null {
  if (!value) return 'passwordRequired';
  if (value.length < minLength) return 'passwordMin';
  return null;
}
